import { ReactNode } from 'react';
import { Group, Stack, Text, Title, Button } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

/**
 * OverviewSection — the titled band the Overview page is built from.
 *
 * Keeps every section heading, subtitle and "View all" affordance identical so the
 * page reads as one document rather than a pile of unrelated cards.
 */
export interface OverviewSectionProps {
  title: string;
  subtitle?: string;
  /** Renders a "View all" button on the right of the heading. */
  viewAllTo?: string;
  viewAllLabel?: string;
  children: ReactNode;
}

export default function OverviewSection({
  title,
  subtitle,
  viewAllTo,
  viewAllLabel = 'View all',
  children,
}: OverviewSectionProps) {
  const navigate = useNavigate();

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-end" wrap="nowrap">
        <div>
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">
            {title}
          </Text>
          {subtitle && (
            <Text size="sm" c="dimmed">
              {subtitle}
            </Text>
          )}
        </div>

        {viewAllTo && (
          <Button
            variant="subtle"
            size="xs"
            rightSection={<IconArrowRight size={14} />}
            onClick={() => navigate(viewAllTo)}
          >
            {viewAllLabel}
          </Button>
        )}
      </Group>

      {children}
    </Stack>
  );
}

/** Larger heading for the two top-level halves of the page (Counselling / Grow). */
export function OverviewHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div>
      <Title order={3}>{title}</Title>
      {subtitle && (
        <Text size="sm" c="dimmed">
          {subtitle}
        </Text>
      )}
    </div>
  );
}
